import React, { useState, useEffect } from 'react';
import { dbService } from '../../lib/supabase';
import { RevenueSetting } from '../../types';
import { HelpCircle } from 'lucide-react';

interface AdSensePlacementProps {
  position: 'home' | 'guides' | 'calculators' | 'state' | 'city' | 'ifsc' | 'guide_detail' | 'calculator_detail' | 'compare' | 'compare_detail' | 'bank_detail';
}

export default function AdSensePlacement({ position }: AdSensePlacementProps) {
  const [setting, setSetting] = useState<RevenueSetting | null>(null);
  const [globalEnabled, setGlobalEnabled] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPlacement() {
      try {
        setLoading(true);
        const settings = await dbService.getRevenueSettings();
        
        // Find global enablement
        const globalSet = settings.find(s => s.position === 'global');
        if (globalSet) {
          setGlobalEnabled(globalSet.enabled);
        }
        
        // Find position-specific enablement
        const posSet = settings.find(s => s.position === position);
        if (posSet) {
          setSetting(posSet);
        }
      } catch (err) {
        console.error('Error loading revenue settings for AdSense:', err);
      } finally {
        setLoading(false);
      }
    }
    loadPlacement();
  }, [position]);

  // Determine isEnabled. We default to true while loading so we can render the placeholder with reserved height immediately and avoid CLS.
  const isEnabled = loading ? true : (globalEnabled && (setting ? setting.enabled : true));

  if (!isEnabled) {
    return null;
  }

  const slotId = setting?.slot_id || 'ca-pub-placeholder';

  // Determine if this placement handles a rectangle format vs a linear banner
  const isRectangle = ['guide_detail', 'calculator_detail', 'compare_detail', 'bank_detail'].includes(position);

  // Strictly reserve height on first layout render cycle to achieve a stable lighthouse CLS score.
  // Full desktop/mobile height constraints.
  const outerHeightClass = isRectangle 
    ? 'min-h-[380px] h-[380px]' 
    : 'min-h-[220px] h-[220px] md:min-h-50 md:h-50';

  const innerHeightClass = isRectangle
    ? 'min-h-[250px] h-[250px]'
    : 'min-h-[90px] h-[90px]';

  return (
    <div 
      id={`adsense-placement-${position}`} 
      className={`my-8 bg-slate-50/50 border border-dashed border-slate-200 rounded-2xl p-4 sm:p-5 flex flex-col items-center justify-center text-center space-y-2.5 max-w-4xl mx-auto overflow-hidden transition-all ${outerHeightClass}`}
    >
      <div className="flex items-center gap-1.5 justify-center h-5 shrink-0">
        <span className="text-[10px] uppercase font-bold tracking-widest text-slate-600">FINSAHAJ SPONSORED BANNER</span>
        <div className="group relative">
          <HelpCircle className="h-3 w-3 text-slate-600 hover:text-slate-800 cursor-help" />
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-48 p-2 rounded bg-slate-900 text-[9px] text-white hidden group-hover:block z-10 shadow-lg text-center leading-normal">
            Finsahaj displays independent, consumer-focused advertisements to keep our educational resources free.
          </div>
        </div>
      </div>

      {/* AdSense Unit Container */}
      <div className={`w-full bg-slate-100/50 rounded-xl flex items-center justify-center border border-slate-200/40 p-2 overflow-hidden shrink-0 ${innerHeightClass}`}>
        {loading ? (
          <div className="flex flex-col items-center justify-center space-y-1">
            <div className="h-3.5 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-3 bg-slate-200/70 rounded w-32 animate-pulse" />
          </div>
        ) : slotId === 'ca-pub-placeholder' || !slotId ? (
          <div className="text-[11px] font-sans text-slate-600 font-medium italic">
            AdSense slot configured. Ready to fetch independent educational sponsor display.
          </div>
        ) : (
          <ins
            className="adsbygoogle"
            style={{ display: 'block', textDecoration: 'none', width: '100%', height: '100%' }}
            data-ad-client={window.location.hostname.includes('finsahaj') ? 'ca-pub-your-real-id' : 'ca-pub-mock'}
            data-ad-slot={slotId}
            data-ad-format={isRectangle ? 'rectangle' : 'horizontal'}
            data-full-width-responsive="true"
          />
        )}
      </div>

      <span className="block text-[8px] text-slate-600 uppercase tracking-widest font-semibold h-3 shrink-0">
        Independent Consumer Information Advocate
      </span>
    </div>
  );
}
